/* eslint-disable no-unused-vars */
import { createContext, PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { Alert, AlertColor, IconButton, Snackbar } from '@mui/material'
import {
  CircleAlertIcon,
  CircleCheckBigIcon,
  OctagonAlertIcon,
  TriangleAlertIcon,
  XIcon
} from 'lucide-react'

// Icone con viewBox corretto (24x24) per compensare il default globale (16x16)
const alertIcons: Record<AlertColor, React.ReactNode> = {
  success: <CircleCheckBigIcon />,
  error: <OctagonAlertIcon />,
  warning: <TriangleAlertIcon />,
  info: <CircleAlertIcon />
}

export type Notification = {
  message: string
  severity: AlertColor
  key: number
}

export type NotificationContextType = {
  notifySuccess: (message: string) => void
  notifyError: (message: string) => void
  notifyWarning: (message: string) => void
  notifyInfo: (message: string) => void
}

export const NotificationContext = createContext<NotificationContextType | null>(null)

type NotificationProviderProps = {
  autoHideDuration?: number
}

function NotificationProvider({
  children,
  autoHideDuration = 5000
}: PropsWithChildren<NotificationProviderProps>) {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const notify = useCallback((message: string, severity: AlertColor) => {
    const key = Date.now()
    setNotifications((prev) => [...prev, { message, severity, key }])
  }, [])

  const handleClose = useCallback((key: number) => {
    setNotifications((prev) => prev.filter((n) => n.key !== key))
  }, [])

  const value: NotificationContextType = useMemo(
    () => ({
      notifySuccess: (message: string) => notify(message, 'success'),
      notifyError: (message: string) => notify(message, 'error'),
      notifyWarning: (message: string) => notify(message, 'warning'),
      notifyInfo: (message: string) => notify(message, 'info')
    }),
    [notify]
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {notifications.map((notification) => (
        <Snackbar
          key={notification.key}
          open
          autoHideDuration={autoHideDuration}
          onClose={() => handleClose(notification.key)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ width: '100%', maxWidth: '35rem' }}
        >
          <Alert
            severity={notification.severity}
            variant="standard"
            icon={alertIcons[notification.severity]}
            sx={{ width: '100%' }}
            action={
              <IconButton
                size="medium"
                color="inherit"
                aria-label="close"
                onClick={() => handleClose(notification.key)}
              >
                <XIcon size={18} />
              </IconButton>
            }
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  )
}

export default NotificationProvider
